import {
	mediaImageConfirmUpload,
	type MediaImageConfirmUploadResponse,
	mediaImageSignUpload,
	type MediaImageSignUploadRequest,
	type MediaImageUploadVariables,
} from '@jobilla/nexus-sdk'
import { useMutation, type UseMutationOptions, type UseMutationResult } from '@tanstack/react-query'
import { logger } from '../../ui'

type AllowedMimeType = MediaImageSignUploadRequest['mimeType']

const ALLOWED_MIME_TYPES: ReadonlyArray<AllowedMimeType> = [
	'image/jpeg',
	'image/png',
	'image/webp',
	'image/svg+xml',
	'image/avif',
]

function detectMimeType(file: File): AllowedMimeType {
	const candidate = file.type as AllowedMimeType
	if (!ALLOWED_MIME_TYPES.includes(candidate)) {
		throw new Error(`Unsupported MIME type: ${file.type || '(empty)'}`)
	}
	return candidate
}

function getFileExtension(filename: string | null | undefined): string | undefined {
	if (typeof filename !== 'string') {
		return undefined
	}
	const lastDot = filename.lastIndexOf('.')
	return lastDot >= 0 ? filename.slice(lastDot + 1).toLowerCase() : undefined
}

type SignedImageUploadError = Error
type UploadStep = 'validate' | 'sign' | 'put' | 'confirm'

// Caps how long the direct-to-GCS PUT can sit before we give up and surface a real
// error. The browser's own TCP timeout is multiple minutes of zero-progress silence —
// long enough that the AssetHub UI looks hung. 10 minutes accommodates a 50 MiB upload
// (the backend ceiling) on a ~700 Kbps uplink while still bounding a fully wedged request.
const PUT_TIMEOUT_MS = 10 * 60 * 1000

export type UploadProgressCallback = (loaded: number, total: number) => void

/**
 * Variables for the signed-upload flow. Narrows the SDK's `media: File | Blob`
 * to `media: File` so callers can't pass a Blob the GCS PUT would have rejected
 * anyway (no name, no auto-detected MIME). Adds an optional `onProgress`
 * callback fired during the direct-to-GCS PUT (lengthComputable progress events
 * from XMLHttpRequest).
 */
export type SignedImageUploadVariables = {
	organisation: MediaImageUploadVariables['organisation']
	data:
		& Omit<MediaImageUploadVariables['data'], 'media' | 'collection'>
		& { media: File; collection: 'uploaded' }
	onProgress?: UploadProgressCallback
	signal?: AbortSignal
}

type PutResult = {
	ok: boolean
	status: number
	statusText: string
	body: string
}

/**
 * Direct PUT to a GCS signed URL using XMLHttpRequest instead of fetch — `fetch`
 * doesn't expose upload progress, XHR does via `upload.onprogress`. Otherwise
 * behaves like the equivalent fetch call: pins headers, honours a hard timeout,
 * resolves with a normalised result regardless of status, rejects only on
 * network / timeout / abort. The optional AbortSignal lets callers abort the
 * in-flight transfer (e.g. on AssetGrid unmount) so we don't keep streaming
 * bytes to GCS for a tab the user already left.
 */
function putToStorage(
	url: string,
	body: File,
	headers: Record<string, string>,
	timeoutMs: number,
	onProgress: UploadProgressCallback | undefined,
	signal: AbortSignal | undefined,
): Promise<PutResult> {
	return new Promise((resolve, reject) => {
		if (signal?.aborted) {
			reject(new DOMException('Direct upload aborted before start', 'AbortError'))
			return
		}

		const xhr = new XMLHttpRequest()
		xhr.open('PUT', url, true)
		xhr.timeout = timeoutMs
		for (const [key, value] of Object.entries(headers)) {
			xhr.setRequestHeader(key, value)
		}
		if (onProgress) {
			xhr.upload.onprogress = (event) => {
				if (event.lengthComputable) {
					onProgress(event.loaded, event.total)
				}
			}
		}

		const onAbort = () => xhr.abort()
		signal?.addEventListener('abort', onAbort)
		const cleanup = () => signal?.removeEventListener('abort', onAbort)

		xhr.onload = () => {
			cleanup()
			resolve({
				ok: xhr.status >= 200 && xhr.status < 300,
				status: xhr.status,
				statusText: xhr.statusText,
				body: xhr.responseText ?? '',
			})
		}
		xhr.onerror = () => {
			cleanup()
			reject(new Error('Network error during direct upload to storage'))
		}
		xhr.ontimeout = () => {
			cleanup()
			reject(new Error(`Direct upload to storage timed out after ${timeoutMs}ms`))
		}
		xhr.onabort = () => {
			cleanup()
			reject(new DOMException('Direct upload aborted', 'AbortError'))
		}

		xhr.send(body)
	})
}

/**
 * Orchestrator for the three-step signed-upload flow:
 *   1. POST /media/:org/images/signed-url → returns a GCS PUT URL
 *   2. PUT the file directly to GCS (plain fetch, bypassing axios timeouts)
 *   3. POST /media/:org/images/confirm    → returns the Media row
 *
 * The variables shape mirrors useMediaImageUpload so callsites can swap drop-in.
 */
export function useSignedImageUpload(
	options?: Omit<
		UseMutationOptions<MediaImageConfirmUploadResponse, SignedImageUploadError, SignedImageUploadVariables>,
		'mutationFn'
	>,
): UseMutationResult<MediaImageConfirmUploadResponse, SignedImageUploadError, SignedImageUploadVariables> {
	return useMutation<MediaImageConfirmUploadResponse, SignedImageUploadError, SignedImageUploadVariables>({
		mutationFn: async ({ organisation, data, onProgress, signal }) => {
			const { id, media, collection, meta } = data

			// `logger.info` is Sentry-breadcrumb-only (does NOT emit to PostHog),
			// so step-transition logs are essentially free observability and only
			// surface when attached to an eventual error event.
			//
			// `fileName` is deliberately omitted from telemetry — it's user-controlled
			// and may contain PII (e.g. "alice_resume.pdf"). `fileExtension` + `fileType`
			// give us the diagnostic signal we'd want from a filename without the PII.
			const tags: { component: string; step: UploadStep } = {
				component: 'SignedImageUpload',
				step: 'validate',
			}
			const breadcrumb: {
				organisation: string
				imageId?: string
				fileSize: number
				fileType: string
				fileExtension?: string
			} = {
				organisation,
				imageId: id ?? undefined,
				fileSize: media.size,
				fileType: media.type,
				fileExtension: getFileExtension(media.name),
			}

			try {
				const mimeType = detectMimeType(media)

				tags.step = 'sign'
				logger.info('SignedImageUpload: sign start', { tags, data: breadcrumb })
				const signed = await mediaImageSignUpload(organisation, {
					id: id ?? null,
					filename: media.name,
					mimeType,
					size: media.size,
					collection,
					meta: meta ?? null,
				}).then((r) => r.data)
				breadcrumb.imageId = signed.id
				logger.info('SignedImageUpload: sign ok', { tags, data: breadcrumb })

				tags.step = 'put'
				logger.info('SignedImageUpload: put start', { tags, data: breadcrumb })
				const putResult = await putToStorage(
					signed.url,
					media,
					signed.requiredHeaders,
					PUT_TIMEOUT_MS,
					onProgress,
					signal,
				)
				if (!putResult.ok) {
					// GCS returns XML <Error><Message>... on failure; capped at 500 chars in telemetry.
					logger.warn('SignedImageUpload: put rejected by storage', {
						tags,
						data: {
							...breadcrumb,
							status: putResult.status,
							statusText: putResult.statusText,
							body: putResult.body.slice(0, 500),
						},
					})
					throw new Error(`Direct upload to storage failed with status ${putResult.status}`)
				}
				logger.info('SignedImageUpload: put ok', { tags, data: breadcrumb })

				tags.step = 'confirm'
				logger.info('SignedImageUpload: confirm start', { tags, data: breadcrumb })
				const confirmed = await mediaImageConfirmUpload(organisation, {
					id: signed.id,
					filename: media.name,
					collection,
					meta: meta ?? null,
				}).then((r) => r.data)
				logger.info('SignedImageUpload: confirm ok', { tags, data: breadcrumb })
				return confirmed
			} catch (error) {
				// AbortErrors are intentional cancellations (component unmounted, caller-driven).
				// They're not failures worth a Sentry exception; rethrow silently so the caller
				// can no-op.
				const isAbort = error instanceof DOMException && error.name === 'AbortError'
				if (!isAbort) {
					logger.error(error, { tags, data: breadcrumb })
				}
				throw error
			}
		},
		...options,
	})
}
